import { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Sparkles, Loader2, Copy, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HistorySection from '../history/HistorySection';

const AIProductDescription = () => {
  const [productName, setProductName] = useState('');
  const [specs, setSpecs] = useState('');
  const [features, setFeatures] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState('casual');
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState('');
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!productName.trim()) {
      setError('Please enter a product name');
      return;
    }

    setGenerating(true);
    setError(null);
    setContent('');
    setStreamingContent('');

    try {
      const featureArray = features
        .split(',')
        .map((f) => f.trim())
        .filter((f) => f);

      const response = await fetch('/api/writing/product-description', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product: {
            name: productName,
            specs,
            features: featureArray,
            targetAudience: audience,
          },
          tone,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate description');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader!.read();
        if (done) break;

        for (const line of decoder.decode(value).split('\n')) {
          if (!line.startsWith('data: ')) continue;
          const data = JSON.parse(line.slice(6));

          if (data.type === 'chunk') {
            setStreamingContent((prev) => prev + (data.content || ''));
          } else if (data.type === 'complete') {
            setContent(data.content || '');
            setStreamingContent('');
          } else if (data.type === 'error') {
            setError(data.error || 'Generation failed');
          }
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    const fullContent = content || streamingContent;
    navigator.clipboard.writeText(fullContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const displayContent = content || streamingContent;

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="text-center space-y-4">
        <div className="inline-flex p-3 bg-gradient-to-br from-[#54c4b6] to-[#216974] rounded-2xl shadow-lg shadow-emerald-200 animate-bounce-subtle">
          <ShoppingBag size={32} className="text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">AI Product Description</h1>
        <p className="text-gray-500 text-lg max-w-lg mx-auto font-medium">
          Generate compelling product copy for websites, e-commerce, and social media.
        </p>
      </div>

      <Card className="overflow-hidden border-0 shadow-2xl shadow-emerald-100">
        <div className="p-8 space-y-6">
          {/* Product Name */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Product Name *</label>
            <Input
              placeholder="e.g., Premium Wireless Headphones Pro"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              className="text-lg"
            />
          </div>

          {/* Specs and Features */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Specifications</label>
              <textarea
                placeholder="e.g., Weight: 200g, Battery: 30hrs, Noise Cancellation"
                value={specs}
                onChange={(e) => setSpecs(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Key Features (comma-separated)</label>
              <textarea
                placeholder="e.g., Active Noise Cancellation, Bluetooth 5.0, Touch Controls"
                value={features}
                onChange={(e) => setFeatures(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-sm"
              />
            </div>
          </div>

          {/* Target Audience & Tone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Target Audience</label>
              <Input
                placeholder="e.g., Tech professionals, Music lovers, Commuters"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Tone</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="trendy">Trendy</option>
                <option value="funny">Funny</option>
                <option value="inspiring">Inspiring</option>
                <option value="authoritative">Authoritative</option>
                <option value="empathetic">Empathetic</option>
                <option value="enthusiastic">Enthusiastic</option>
                <option value="informative">Informative</option>
                <option value="sarcastic">Sarcastic</option>
                <option value="serious">Serious</option>
                <option value="friendly">Friendly</option>
                <option value="formal">Formal</option>
                <option value="informal">Informal</option>
                <option value="persuasive">Persuasive</option>
                <option value="educational">Educational</option>
                <option value="entertaining">Entertaining</option>
                <option value="nostalgic">Nostalgic</option>
                <option value="urgent">Urgent</option>
                <option value="mysterious">Mysterious</option>
                <option value="bold">Bold</option>
                <option value="minimalist">Minimalist</option>
                <option value="luxury">Luxury</option>
                <option value="playful">Playful</option>
                <option value="sincere">Sincere</option>
                <option value="witty">Witty</option>
                <option value="curious">Curious</option>
              </select>
            </div>
          </div>

          {/* Error Alert */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-semibold flex items-center gap-2"
              >
                <span>⚠️</span>
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            isLoading={generating}
            className="w-full h-12 bg-gradient-to-r from-[#54c4b6] to-[#216974] hover:scale-105 transition-transform text-white font-bold text-lg rounded-xl flex items-center justify-center gap-2"
            leftIcon={generating ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
          >
            {generating ? 'Generating Descriptions...' : 'Generate Descriptions'}
          </Button>
        </div>
      </Card>

      {/* Generated Content */}
      <AnimatePresence>
        {displayContent && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}>
            <Card className="border-0 shadow-xl p-8 space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Generated Descriptions</h2>
                <Button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 bg-[#54c4b6] hover:bg-[#54c4b6]/10 text-[#216974] rounded-lg font-semibold transition-colors"
                  leftIcon={<Copy size={16} />}
                >
                  {copied ? 'Copied!' : 'Copy All'}
                </Button>
              </div>

              <div className="space-y-6">
                {displayContent.split('---').map((section, idx) => {
                  const trimmed = section.trim();
                  if (!trimmed) return null;

                  const lines = trimmed.split('\n');
                  const title = lines[0];
                  const body = lines.slice(1).join('\n');

                  const sectionBg = ['bg-[#216974]', 'bg-[#e4c084]', 'bg-[#216974]'][idx];
                  const sectionBorder = ['border-[#54c4b6]', 'border-orange-200', 'border-[#54c4b6]'][idx];

                  return (
                    <div key={idx} className={`p-6 rounded-lg border ${sectionBg} ${sectionBorder}`}>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">{title}</h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{body.trim()}</p>
                    </div>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <HistorySection toolId="ai-product" />
    </div>
  );
};

export default AIProductDescription;
